import { fiscalPairOfYearWeek, yearWeekToIsoDate } from "@appretio/shared";
import type { ColDef } from "ag-grid-enterprise";
import {
  resolvePeriodLabel,
  WEEKLY_KEYS
} from "../../../../utils/timePeriodUtils";
import type { TimePeriodColumnOptions } from "../columnFactory";
import { baseTimePeriodProperties } from "./baseTimePeriodProperties";
import { getEditableByPlanBy } from "./editableByPlanBy";

/**
 * Creates the 53 weekly column definitions (W01–W53).
 * The 53 weeks are a 454 calendar convention, see Confluence for details.
 */
export function createWeeklyColumns(
  options: TimePeriodColumnOptions
): ColDef[] {
  const {
    planBy,
    planningYear,
    metaData,
    weekLabels,
    intake,
    editableProducer,
    editableRowPredicate,
    editableMetricsOverride,
    editableTimeWindowPredicate,
    headerTooltipYearWeekResolver,
    cubeWindow
  } = options;
  const base = baseTimePeriodProperties(
    planBy,
    planningYear,
    metaData,
    cubeWindow,
    editableTimeWindowPredicate !== undefined
  );
  const editable =
    planBy === "SH%"
      ? false
      : getEditableByPlanBy({
          planBy,
          planningYear,
          metaData,
          intake,
          editableProducer,
          editableRowPredicate,
          editableMetricsOverride,
          editableTimeWindowPredicate,
          cubeWindow
        });

  return WEEKLY_KEYS.map((fieldId) => {
    const yearWeek = headerTooltipYearWeekResolver?.(fieldId);
    return {
      ...base,
      field: fieldId,
      headerName: resolvePeriodLabel(fieldId, weekLabels, metaData.gregMapping),
      headerTooltip: buildWeeklyHeaderTooltip({
        fiscalWeekLabel: fieldId,
        calendarWeekLabel: metaData.gregMapping[fieldId],
        yearWeek
      }),
      editable
    };
  });
}

/**
 * Tooltip for a weekly column header: `Calendar W## • Fiscal W##`, plus
 * the ISO Mon→Sun dates and fiscal-year pair when `yearWeek` is given.
 *
 * Direction is explicit in the arguments: callers resolve their own
 * column id against `gregMapping` (which maps FISCAL keys to CALENDAR
 * labels) and pass both labels. Omit `yearWeek` when the header spans
 * multiple fiscal years (a single date range can't speak for every row).
 */
export function buildWeeklyHeaderTooltip(args: {
  /** Calendar (Gregorian) week label, e.g. `W32`. */
  calendarWeekLabel: string | undefined;
  /** Fiscal week label, e.g. `W01`. */
  fiscalWeekLabel: string | undefined;
  /** Calendar `YYYYWW` driving the date range + FY tag. */
  yearWeek?: number;
}): string {
  const { calendarWeekLabel, fiscalWeekLabel, yearWeek } = args;
  let base: string;
  if (
    calendarWeekLabel &&
    fiscalWeekLabel &&
    calendarWeekLabel !== fiscalWeekLabel
  ) {
    base = `Calendar ${calendarWeekLabel} • Fiscal ${fiscalWeekLabel}`;
  } else if (calendarWeekLabel) {
    base = `Calendar ${calendarWeekLabel}`;
  } else if (fiscalWeekLabel) {
    base = `Fiscal ${fiscalWeekLabel}`;
  } else {
    return "";
  }
  if (yearWeek === undefined) return base;
  // Malformed yearWeek (0, NaN, out-of-range week) leaves the range
  // empty — don't pretend to know the dates or FY pair in that case.
  const range = formatIsoWeekRange(yearWeek);
  if (!range) return base;
  const fiscalPair = fiscalPairOfYearWeek(yearWeek);
  const tagged = fiscalPair ? `${range} (FY ${fiscalPair})` : range;
  return `${base} • ${tagged}`;
}

/**
 * Formats the ISO Monday→Sunday date range for a `YYYYWW` year-week as
 * `Mon YYYY-MM-DD → Sun YYYY-MM-DD`. Returns the empty string for
 * non-positive or malformed input so callers can append unconditionally.
 */
function formatIsoWeekRange(yearWeek: number): string {
  const monday = yearWeekToIsoDate(yearWeek);
  if (!monday) return "";
  const sunday = new Date(`${monday}T00:00:00Z`);
  sunday.setUTCDate(sunday.getUTCDate() + 6);
  return `Mon ${monday} → Sun ${sunday.toISOString().slice(0, 10)}`;
}
